// Jetty placement, boat, cart, and their storm damage/repair.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.
//
// Unlike earlier systems, economy.ts, fishing.ts, and player.ts already
// exist by this step, so this module imports their real exports directly
// (woodCount/spendWood, cast/endCast, player) instead of stubbing them.
// It does NOT own `scene`: newly created meshes (jetty decks, the boat,
// the cart, ghost previews, oar splashes) are added to groups exported
// here, and main.ts adds each group to the scene once during wiring.

import * as THREE from 'three';
import {
  HEX_R, HEX_H, PROP_SCALE, SEA_LEVEL, HEX_SIDE_DIRS, hexToWorld, worldToHex, terrainHeightAt, isWaterAt, surfaceYAt,
} from '@world/terrain';
import { WATER_SURF_Y, isShallowWaterAt } from '@world/water';
import { buildHexPrism } from '@world/hexPrism';
import { toonMat, addOutlines } from '@world/toon';
import { S, washTile, toTexture } from '@world/wash';
import { fishGeo, fishMats } from '@entities/fish';
import { player } from '@entities/player';
import { cast, endCast } from '@systems/fishing';
import {
  woodCount, spendWood, transferFish, incrementJettyCount,
  CARRY_CAP, CART_CAP, carryFish, boatFish, cartFish,
} from '@economy/market';

function angleDiff(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

// Shared materials/geometry across jetty, boat, and cart woodwork.
const plankMat = toonMat(0x9c8259);
const plankDarkMat = toonMat(0x8a7250);
const pilingMat = toonMat(0x6f5c42);
const JETTY_WIDTH = 0.41;
const JETTY_SPAN_LEN = 1.07; // hex-to-hex, so consecutive sections meet

// Separate boards with real gaps between them, so the water shows through
// the decking. Narrow ones: twelve across the span rather than the original
// six, which halves the board width and reads as older, rougher timber.
const DECK_BOARDS = 12;
const BOARD_PITCH = JETTY_SPAN_LEN / DECK_BOARDS;
const BOARD_W = BOARD_PITCH * 0.78; // the remainder is the gap

// Grain only — no joints drawn in, since the gaps are real geometry now.
function texDeckBoard(): THREE.CanvasTexture {
  const c = washTile('#a98f66', ['#9c8259', '#b89b73', '#8e7750'], '#7d6743', '#6b573a');
  const ctx = c.getContext('2d')!;
  for (let i = 0; i < S * 2.2; i++) {
    ctx.fillStyle = ['rgba(110,90,60,0.35)', 'rgba(200,178,140,0.3)'][Math.floor(Math.random() * 2)];
    const len = 6 + Math.random() * 16;
    ctx.fillRect(Math.random() * S, Math.random() * (S - len), 1, len);
  }
  return toTexture(c, true, 1.4);
}

// Built already oriented: X is the board's thickness along the pier, Z its
// length across the walkway, so no per-board rotation is needed.
// Where the runners sit across the width — and, since the runners are
// carried on them, where the pilings and their cross tie go too.
const LEG_Z = JETTY_WIDTH * 0.26;

const plankGeo = new THREE.BoxGeometry(BOARD_W, 0.04, JETTY_WIDTH);
const boardTex = texDeckBoard();
const deckMat = toonMat(0xf2efe8);
deckMat.map = boardTex;
const deckMatDark = toonMat(0xdbd4c7);
deckMatDark.map = boardTex;
const postGeo = new THREE.CylinderGeometry(0.032, 0.042, 1, 5);

// ================= Jetty & Boat =================
export const JETTY_COST = 12, BOAT_COST = 20;
// Expressed as fractions of a terrain layer so the pier keeps sitting the
// same distance proud of the water when HEX_H changes.
const DECK_CENTER_Y = WATER_SURF_Y + HEX_H * 0.27;
const DECK_TOP_Y = DECK_CENTER_Y + HEX_H * 0.09;

export const jettyGroup = new THREE.Group();
// "q,r" hex key -> the deck's heading. The angle is kept because the deck
// is only half a hex wide, so walkability has to be tested against the
// planks rather than the whole hex, and that needs to know which way the
// boards run.
const jettyDecks = new Map<string, number>();

export function addJettyDeck(q: number, r: number, angle: number): void {
  const pos = hexToWorld(q, r);
  const g = new THREE.Group();
  g.position.set(pos.x, DECK_CENTER_Y, pos.z);
  g.rotation.y = -angle;
  // Built the way a real boardwalk is: two runners along the length,
  // carried on the pilings, with the cross boards laid over them. The
  // runners sit proud enough to be seen through the gaps between boards.
  ([LEG_Z, -LEG_Z] as const).forEach((oz) => {
    const runner = new THREE.Mesh(new THREE.BoxGeometry(JETTY_SPAN_LEN, 0.04, 0.055), pilingMat);
    runner.position.set(0, -0.04, oz);
    g.add(runner);
  });
  // Alternating tone and a hair of height variation, so the boards read as
  // laid by hand rather than milled to match.
  for (let i = 0; i < DECK_BOARDS; i++) {
    const board = new THREE.Mesh(plankGeo, i % 2 ? deckMatDark : deckMat);
    board.position.set(-JETTY_SPAN_LEN / 2 + BOARD_PITCH * (i + 0.5), (i % 2) * 0.004, 0);
    board.userData.isDeckBoard = true; // applyJettyDamageVisual matches on this
    g.add(board);
  }

  // Piling-posts: from the lakebed up past the deck. Set well inboard so
  // the pair reads as a narrow trestle under the walkway rather than
  // straddling its full width.
  const bedTopY = terrainHeightAt(q, r) * HEX_H + HEX_H / 2;
  const topY = DECK_TOP_Y + 0.17;
  const pilH = Math.max(0.2, topY - bedTopY);
  ([LEG_Z, -LEG_Z] as const).forEach((oz) => {
    const pil = new THREE.Mesh(postGeo, pilingMat);
    pil.scale.y = pilH;
    pil.position.set(0, (bedTopY + pilH / 2) - DECK_CENTER_Y, oz);
    g.add(pil);
  });
  // Cross tie between the two pilings, just under the deck — reads as the
  // bracing that holds a pier's legs together when seen from the water.
  const tie = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.04, LEG_Z * 2), pilingMat);
  tie.position.y = -0.08;
  g.add(tie);
  // Edge boards rather than handrails: a low kerb laid flat along each side,
  // which trims the deck without a fence standing between the camera and
  // the walkway.
  const edge = JETTY_WIDTH / 2 - 0.022;
  ([edge, -edge] as const).forEach((oz) => {
    const trim = new THREE.Mesh(new THREE.BoxGeometry(JETTY_SPAN_LEN, 0.022, 0.04), plankDarkMat);
    trim.position.set(0, 0.028, oz);
    g.add(trim);
  });
  addOutlines(g);
  jettyGroup.add(g);
  jettyDecks.set(q + ',' + r, angle);
}

// Only the planks are walkable, not the whole hex they sit in: the deck is
// half a hex wide, so without the band test the fisherman could walk to the
// hex edge and stand on open water beside the pier.
const JETTY_HALF_BAND = JETTY_WIDTH / 2 + 0.04; // slight tolerance to step aboard
export function onJetty(x: number, z: number): boolean {
  const h = worldToHex(x, z);
  const angle = jettyDecks.get(h.q + ',' + h.r);
  if (angle === undefined) return false;
  // addJettyDeck rotates the deck by -angle, which lays its length along
  // (cos, sin) and its width along (-sin, cos) — so this is the offset from
  // the centreline, across the boards.
  const c = hexToWorld(h.q, h.r);
  const perp = -(x - c.x) * Math.sin(angle) + (z - c.z) * Math.cos(angle);
  return Math.abs(perp) <= JETTY_HALF_BAND;
}
export function canStand(x: number, z: number): boolean {
  return !isWaterAt(x, z) || onJetty(x, z);
}
export function standY(x: number, z: number): number {
  return onJetty(x, z) ? DECK_TOP_Y : surfaceYAt(x, z);
}

// Movement-only allowance for the fisherman on foot: everywhere canStand
// already allows, plus wading into the shallowest water — but only when
// approaching from dry land/water, never by stepping off a jetty deck
// (a jetty's own hex can itself be shallow water, so this needs the
// "from" tile, unlike canStand/standY above). Cart-pulling and shelter
// placement intentionally keep using plain canStand — only the player
// himself wades.
export function canWade(fromX: number, fromZ: number, toX: number, toZ: number): boolean {
  if (canStand(toX, toZ)) return true;
  return isShallowWaterAt(toX, toZ) && !onJetty(fromX, fromZ);
}

// True only when actually standing in the water, not on a jetty deck that
// happens to be built over a shallow hex — callers use this to slow
// walking speed and block rough sleep while wading.
export function isWading(x: number, z: number): boolean {
  return isShallowWaterAt(x, z) && !onJetty(x, z);
}

// ---- Jetty placement: the player chooses the direction ----
export let placingJetty = false;
export interface JettyPlan {
  dir: readonly [number, number];
  cells: Array<{ q: number; r: number }>;
  angle: number;
}
let jettyPlan: JettyPlan | null = null;

const ghostMat = new THREE.MeshBasicMaterial({ color: 0xffe9a8, transparent: true, opacity: 0.45, depthWrite: false });
const ghostGeo = buildHexPrism(HEX_R * 0.95, 0.05);
export const ghostGroup = new THREE.Group();
// How many hexes of water a single jetty reaches out over.
const JETTY_SPAN = 2;
const ghostMeshes = Array.from({ length: JETTY_SPAN }, () => {
  const m = new THREE.Mesh(ghostGeo, ghostMat);
  m.visible = false;
  ghostGroup.add(m);
  return m;
});

const jettyRaycaster = new THREE.Raycaster();

export function beginJettyPlacement(): { success: boolean; reason?: string } {
  if (woodCount() < JETTY_COST) return { success: false, reason: 'not-enough-wood' };
  const p = worldToHex(player.x, player.z);
  if (terrainHeightAt(p.q, p.r) < SEA_LEVEL) return { success: false, reason: 'not-on-shore' };
  placingJetty = true;
  return { success: true };
}

export function cancelJettyPlacement(): void {
  placingJetty = false;
  jettyPlan = null;
  ghostMeshes.forEach((m) => { m.visible = false; });
}

// Aim the jetty from the camera/pointer ray (mouseNDC) against the water plane.
export function updateJettyGhost(camera: THREE.Camera, mouseNDC: THREE.Vector2): void {
  if (!placingJetty) return;
  jettyPlan = null;
  ghostMeshes.forEach((m) => { m.visible = false; });
  jettyRaycaster.setFromCamera(mouseNDC, camera);
  const o = jettyRaycaster.ray.origin, d = jettyRaycaster.ray.direction;
  if (Math.abs(d.y) < 1e-4) return;
  const t = (WATER_SURF_Y - o.y) / d.y;
  if (t <= 0) return;
  const px = o.x + d.x * t, pz = o.z + d.z * t;
  // Choose the water-side hex edge best aligned with the aim
  const p = worldToHex(player.x, player.z);
  // beginJettyPlacement() only checked this once, at the moment placement
  // started — but the player can keep walking/wading while aiming, before
  // ever confirming. Re-check every frame so the ghost (and therefore any
  // resulting jetty) never anchors from a hex that's drifted off shore;
  // otherwise a jetty could end up with no end touching land at all,
  // stranding whoever walks out onto it.
  if (terrainHeightAt(p.q, p.r) < SEA_LEVEL) return;
  const aim = Math.atan2(pz - player.z, px - player.x);
  let best: readonly [number, number] | null = null, bestDot = -2;
  HEX_SIDE_DIRS.forEach(([dq, dr]) => {
    const q = p.q + dq, r = p.r + dr;
    if (terrainHeightAt(q, r) >= SEA_LEVEL || jettyDecks.has(q + ',' + r)) return;
    const w = hexToWorld(dq, dr);
    const len = Math.hypot(w.x, w.z);
    const dot = (w.x / len) * Math.cos(aim) + (w.z / len) * Math.sin(aim);
    if (dot > bestDot) { bestDot = dot; best = [dq, dr]; }
  });
  if (!best) return;
  const [bq, br] = best as [number, number];
  const cells: Array<{ q: number; r: number }> = [];
  for (let i = 1; i <= JETTY_SPAN; i++) {
    const q = p.q + bq * i, r = p.r + br * i;
    if (terrainHeightAt(q, r) >= SEA_LEVEL || jettyDecks.has(q + ',' + r)) break;
    cells.push({ q, r });
  }
  if (!cells.length) return;
  const bw = hexToWorld(bq, br);
  jettyPlan = { dir: best!, cells, angle: Math.atan2(bw.z, bw.x) };
  cells.forEach((c, i) => {
    const pos = hexToWorld(c.q, c.r);
    ghostMeshes[i].position.set(pos.x, DECK_CENTER_Y, pos.z);
    ghostMeshes[i].rotation.y = -jettyPlan!.angle;
    ghostMeshes[i].scale.set(1, 1, JETTY_WIDTH);
    ghostMeshes[i].visible = true;
  });
}

export function confirmJettyPlacement(): {
  success: boolean; reason?: string; cells?: Array<{ q: number; r: number }>; angle?: number;
} {
  if (!jettyPlan) return { success: false, reason: 'no-plan' };
  const { cells, angle } = jettyPlan;
  cells.forEach((c) => addJettyDeck(c.q, c.r, angle));
  incrementJettyCount();
  spendWood(JETTY_COST);
  cancelJettyPlacement();
  return { success: true, cells, angle };
}

// ---- Boat ----
export interface Boat {
  mesh: THREE.Group;
  x: number;
  z: number;
  heading: number;
  rowPhase: number;
  lastSin: number;
}
// Boat and cart meshes are created lazily at runtime (on purchase/build),
// unlike the module's other groups. main.ts adds this group to the scene
// once; tryBuildBoat/spawnCart add their mesh to it internally, so callers
// (including hud.ts's market panel, which doesn't own `scene`) don't need
// to remember to register the new mesh themselves.
export const vehicleGroup = new THREE.Group();

export let boat: Boat | null = null;
export let inBoat = false;
export let BOAT_SPEED = 2.3; // grows with boat upgrades
// The hull sits at the common prop scale but is stretched along its length:
// a traditional coastal fishing boat is long and narrow, and at the modelled
// proportions it read as a tub. Only X is stretched, so freeboard and beam
// stay in proportion to the fisherman sitting in it.
export const BOAT_STRETCH = 2;
// Draft follows the hull's own scale, not the terrain unit — a fixed dip
// would swallow it. Vertical scale is untouched by the stretch.
export const BOAT_FLOAT_Y = WATER_SURF_Y - 0.045 * PROP_SCALE;

export function increaseBoatSpeed(amount: number): void {
  BOAT_SPEED += amount;
}

// The hull is long, so its bow/stern can overhang a shore tile even
// when its centre is over water — which made land poke up through the
// boat. Movement checks the hull's extremities too.
const HULL_SAMPLES: ReadonlyArray<readonly [number, number]> = [[0.66, 0], [-0.56, 0], [0, 0.27], [0, -0.27]];
export function boatSpotClear(x: number, z: number, heading: number): boolean {
  if (!isWaterAt(x, z) || onJetty(x, z)) return false;
  const c = Math.cos(heading), s = Math.sin(heading);
  for (const [lx, lz] of HULL_SAMPLES) {
    if (!isWaterAt(x + lx * c - lz * s, z + lx * s + lz * c)) return false;
  }
  return true;
}

// Wasen-style flat-bottomed hull: pointed raised bow, flared sides,
// flat stern transom. One custom geometry (~13 triangles), open-topped
// so you can see inside the boat.
function buildHullGeometry(): THREE.BufferGeometry {
  const B = 0, T = 0.2;
  const V: Record<string, [number, number, number]> = {
    b1: [-0.5, B, -0.15], b2: [0.42, B, -0.15], b3: [0.42, B, 0.15], b4: [-0.5, B, 0.15],
    t1: [-0.58, T, -0.26], t2: [0.5, T, -0.27], t3: [0.5, T, 0.27], t4: [-0.58, T, 0.26],
    bow: [0.72, T + 0.04, 0],
    bowB: [0.56, B + 0.05, 0],
  };
  const tris = [
    ['b1', 'b3', 'b2'], ['b1', 'b4', 'b3'],           // flat bottom
    ['b1', 'b2', 't2'], ['b1', 't2', 't1'],           // port side
    ['b4', 't3', 'b3'], ['b4', 't4', 't3'],           // starboard side
    ['b1', 't1', 't4'], ['b1', 't4', 'b4'],           // stern transom
    ['b2', 'b3', 'bowB'],                             // bow underside
    ['b2', 'bowB', 'bow'], ['b2', 'bow', 't2'],       // bow port
    ['b3', 'bow', 'bowB'], ['b3', 't3', 'bow'],       // bow starboard
  ];
  const positions: number[] = [];
  tris.forEach((t) => t.forEach((n) => positions.push(...V[n])));
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  return geo;
}
// The interior sole, cut to the hull's cross-section at the height it sits.
// The hull runs from a flat bottom at y=0 (z = ±0.15) out to the gunwale at
// y=0.2 (z = ±0.265), so the panel's width is interpolated at its own
// height rather than matching the bottom — that flare is exactly the gap
// the old rectangular floorboards left water showing through.
function buildSoleGeometry(): THREE.BufferGeometry {
  const y = 0.075;
  const f = y / 0.2;
  const xa = -0.5 + (-0.58 + 0.5) * f;   // stern, following b1 -> t1
  const xf = 0.42 + (0.5 - 0.42) * f;    // amidships-forward, b2 -> t2
  const z = 0.15 + (0.265 - 0.15) * f;
  const xbow = 0.56 + (0.72 - 0.56) * ((y - 0.05) / (0.24 - 0.05));
  const P: Record<string, [number, number, number]> = {
    ap: [xa, y, -z], fp: [xf, y, -z], fs: [xf, y, z], as: [xa, y, z], bow: [xbow, y, 0],
  };
  const tris = [['ap', 'fp', 'fs'], ['ap', 'fs', 'as'], ['fp', 'bow', 'fs']];
  const positions: number[] = [];
  tris.forEach((t) => t.forEach((n) => positions.push(...P[n])));
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  return geo;
}

// Exported for reuse by the (similarly-shaped, larger) shipwreck hulls in world/chunks.ts.
export const hullGeo = buildHullGeometry();

export function makeBoatMesh(): THREE.Group {
  const g = new THREE.Group();
  const hullMat = toonMat(0x8a6f4c);
  hullMat.side = THREE.DoubleSide; // open hull shell, seen from inside too
  const hull = new THREE.Mesh(hullGeo, hullMat);
  g.add(hull);
  // A solid sole across the whole interior, above the waterline, so the
  // transparent water surface never renders inside the open hull. Two
  // rectangular boards couldn't do this: the hull flares outward as it
  // rises, so a board matching the flat bottom leaves a wedge of water
  // showing along each side at the height the water plane actually cuts
  // through. This panel is cut to the hull's own cross-section instead.
  const floorMat = toonMat(0x7f6647);
  floorMat.side = THREE.DoubleSide;
  g.add(new THREE.Mesh(buildSoleGeometry(), floorMat));
  // Planking laid over it, for the look the boards were giving before.
  const boardMat = toonMat(0x8a6f4e);
  [-0.42, -0.18, 0.06, 0.3].forEach((bx) => {
    const b = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.012, 0.3), boardMat);
    b.position.set(bx, 0.083, 0);
    g.add(b);
  });
  // Gunwale trim along both rims
  const trimGeo = new THREE.BoxGeometry(1.1, 0.03, 0.05);
  [1, -1].forEach((side) => {
    const trim = new THREE.Mesh(trimGeo, pilingMat);
    trim.position.set(-0.04, 0.21, side * 0.265);
    g.add(trim);
  });
  // Two plank benches across the hull
  [0.08, -0.3].forEach((bx) => {
    const bench = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.03, 0.46), plankMat);
    bench.position.set(bx, 0.13, 0);
    g.add(bench);
  });
  // Woven creel lashed across the bow half — the barrel-shaped bundle is
  // the most recognisable thing on the reference boat. It sits forward of
  // the fish slots on purpose, so the visible catch isn't hidden under it.
  const creelMat = toonMat(0xbda878);
  const creel = new THREE.Mesh(new THREE.CylinderGeometry(0.135, 0.135, 0.34, 10), creelMat);
  creel.rotation.z = Math.PI / 2;
  creel.position.set(0.3, 0.2, 0);
  g.add(creel);
  const bandMat = toonMat(0x8e7a4e);
  [-0.12, 0, 0.12].forEach((bx) => {
    const band = new THREE.Mesh(new THREE.TorusGeometry(0.138, 0.011, 4, 10), bandMat);
    band.rotation.y = Math.PI / 2;
    band.position.set(0.3 + bx, 0.2, 0);
    g.add(band);
  });
  // A second strake below the gunwale, so the hull reads as planked
  [1, -1].forEach((side) => {
    const strake = new THREE.Mesh(new THREE.BoxGeometry(1.02, 0.022, 0.03), plankDarkMat);
    strake.position.set(-0.04, 0.135, side * 0.253);
    g.add(strake);
  });
  // Catch shown lying on the floorboards (each fish stands for up to 2)
  g.userData.fishSlots = [];
  for (let i = 0; i < 10; i++) {
    const f = new THREE.Mesh(fishGeo, fishMats[0]);
    f.scale.setScalar(0.12);
    f.position.set(-0.38 + (i % 5) * 0.1, 0.085 + Math.floor(i / 5) * 0.012, (i < 5 ? -0.08 : 0.07));
    f.rotation.y = i * 1.1;
    f.visible = false;
    g.userData.fishSlots.push(f);
    g.add(f);
  }
  // Oars resting over the gunwales
  const oarGeo = new THREE.CylinderGeometry(0.012, 0.012, 0.75, 4);
  g.userData.oars = [];
  [1, -1].forEach((side) => {
    const oar = new THREE.Mesh(oarGeo, pilingMat);
    oar.position.set(-0.05, 0.2, side * 0.24);
    oar.rotation.x = side * 1.25;
    oar.userData.baseRotX = side * 1.25;
    oar.userData.side = side;
    g.userData.oars.push(oar);
    g.add(oar);
  });
  // Yaw first, then tilt in the hull's own frame, so pitch and roll mean
  // what they should regardless of which way she's pointing. Under the
  // default XYZ order the tilt would be applied in world axes instead.
  g.rotation.order = 'YXZ';
  g.scale.set(PROP_SCALE * BOAT_STRETCH, PROP_SCALE, PROP_SCALE);
  addOutlines(g);
  return g;
}

// A pennant to show her new rank (added once, when the boat upgrade succeeds).
export function addBoatUpgradeDecor(): void {
  if (!boat) return;
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.015, 0.42, 4), pilingMat);
  mast.position.set(-0.48, 0.38, 0);
  boat.mesh.add(mast);
  const flagMat = toonMat(0xb35646);
  flagMat.side = THREE.DoubleSide;
  const flag = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.09), flagMat);
  flag.position.set(-0.4, 0.54, 0);
  boat.mesh.add(flag);
}

// ---- Oar splash effect (small pooled ripples) ----
interface Splash {
  mesh: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  life: number;
  active: boolean;
}
const splashGeo = new THREE.SphereGeometry(0.05, 5, 4);
const splashes: Splash[] = [];
export const splashGroup = new THREE.Group();

export function spawnSplash(x: number, z: number): void {
  let s = splashes.find((sp) => !sp.active);
  if (!s) {
    if (splashes.length >= 12) return;
    const mat = new THREE.MeshBasicMaterial({ color: 0xeaf6ff, transparent: true, opacity: 0.8 });
    const mesh = new THREE.Mesh(splashGeo, mat);
    mesh.visible = false;
    splashGroup.add(mesh);
    s = { mesh, mat, life: 0, active: false };
    splashes.push(s);
  }
  s.active = true;
  s.life = 0.45;
  s.mesh.visible = true;
  s.mesh.position.set(x, WATER_SURF_Y + 0.01, z);
}

export function updateSplashes(dt: number): void {
  splashes.forEach((s) => {
    if (!s.active) return;
    s.life -= dt;
    if (s.life <= 0) { s.active = false; s.mesh.visible = false; return; }
    const t = 1 - s.life / 0.45;
    const sc = 0.6 + t * 1.6;
    s.mesh.scale.set(sc, 0.25 * (1 - t * 0.5), sc);
    s.mat.opacity = 0.8 * (1 - t);
  });
}
export const oarTipLocal = new THREE.Vector3();

export function tryBuildBoat(): { success: boolean; reason?: string } {
  if (jettyDamaged) return { success: false, reason: 'jetty-damaged' };
  if (woodCount() < BOAT_COST) return { success: false, reason: 'not-enough-wood' };
  const p = worldToHex(player.x, player.z);
  let spot: { x: number; z: number } | null = null, fallback: { x: number; z: number } | null = null;
  for (const [dq, dr] of HEX_SIDE_DIRS) {
    const q = p.q + dq, r = p.r + dr;
    if (terrainHeightAt(q, r) < SEA_LEVEL && !jettyDecks.has(q + ',' + r)) {
      const pos2 = hexToWorld(q, r);
      if (!fallback) fallback = pos2;
      if (!spot && boatSpotClear(pos2.x, pos2.z, player.heading)) spot = pos2;
    }
  }
  const pos = spot || fallback;
  if (!pos) return { success: false, reason: 'no-open-water' };
  boat = { mesh: makeBoatMesh(), x: pos.x, z: pos.z, heading: player.heading, rowPhase: 0, lastSin: 0 };
  boat.mesh.position.set(pos.x, BOAT_FLOAT_Y, pos.z);
  vehicleGroup.add(boat.mesh);
  spendWood(BOAT_COST);
  return { success: true };
}

export function boardBoat(): { success: boolean; reason?: string } {
  if (!boat) return { success: false, reason: 'no-boat' };
  if (boatDamaged) return { success: false, reason: 'boat-damaged' };
  if (cast.active) endCast();
  cartGrabbed = false;
  inBoat = true;
  return { success: true };
}

export function findDisembarkSpot(): { x: number; z: number } | null {
  if (!boat) return null;
  const p = worldToHex(boat.x, boat.z);
  for (const [dq, dr] of HEX_SIDE_DIRS) {
    const q = p.q + dq, r = p.r + dr;
    if (terrainHeightAt(q, r) >= SEA_LEVEL || jettyDecks.has(q + ',' + r)) {
      return hexToWorld(q, r);
    }
  }
  return null;
}

export function disembark(): { success: boolean; reason?: string; fishCarriedAshore: number } {
  const spot = findDisembarkSpot();
  if (!spot) return { success: false, reason: 'no-spot', fishCarriedAshore: 0 };
  if (cast.active) endCast();
  inBoat = false;
  player.x = spot.x;
  player.z = spot.z;
  // With no cart waiting, he carries what his basket can hold ashore
  const cartAdjacent = !!(cart && Math.hypot(cart.x - boat!.x, cart.z - boat!.z) < 2.6);
  let fishCarriedAshore = 0;
  if (boatFish.length && !cartAdjacent) {
    fishCarriedAshore = transferFish(boatFish, carryFish, CARRY_CAP);
  }
  return { success: true, fishCarriedAshore };
}

// ---- Cart (bought at a village market; pulled behind him on land) ----
export interface Cart {
  mesh: THREE.Group;
  x: number;
  z: number;
  heading: number;
}
export let cart: Cart | null = null;
export let cartGrabbed = false;

const wheelGeo = new THREE.CylinderGeometry(0.11, 0.11, 0.045, 8);
wheelGeo.rotateX(Math.PI / 2);

export function makeCartMesh(): THREE.Group {
  const g = new THREE.Group();
  const bed = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.05, 0.34), plankMat);
  bed.position.y = 0.16;
  g.add(bed);
  [1, -1].forEach((s) => {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.09, 0.03), pilingMat);
    rail.position.set(0, 0.23, s * 0.165);
    g.add(rail);
  });
  const back = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.09, 0.34), pilingMat);
  back.position.set(-0.27, 0.23, 0);
  g.add(back);
  g.userData.wheels = [];
  [1, -1].forEach((s) => {
    const wl = new THREE.Mesh(wheelGeo, pilingMat);
    wl.position.set(0, 0.11, s * 0.2);
    g.userData.wheels.push(wl);
    g.add(wl);
  });
  [1, -1].forEach((s) => {
    const h = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.42, 4), pilingMat);
    h.rotation.z = -1.3;
    h.position.set(0.44, 0.19, s * 0.12);
    g.add(h);
  });
  // Hauled fish shown in the bed (each fish stands for up to 2)
  g.userData.fishSlots = [];
  for (let i = 0; i < 10; i++) {
    const f = new THREE.Mesh(fishGeo, fishMats[0]);
    f.scale.setScalar(0.12);
    f.position.set(-0.2 + (i % 5) * 0.1, 0.21 + Math.floor(i / 5) * 0.015, (i < 5 ? -0.08 : 0.06));
    f.rotation.y = i * 1.3;
    f.visible = false;
    g.userData.fishSlots.push(f);
    g.add(f);
  }
  g.scale.setScalar(PROP_SCALE);
  addOutlines(g);
  return g;
}

// Taller side rails (added once, when the cart upgrade succeeds).
export function addCartUpgradeDecor(): void {
  if (!cart) return;
  [1, -1].forEach((s) => {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.08, 0.03), pilingMat);
    rail.position.set(0, 0.31, s * 0.165);
    cart!.mesh.add(rail);
  });
}

export function spawnCart(x: number, z: number, heading: number): void {
  cart = { mesh: makeCartMesh(), x, z, heading };
  vehicleGroup.add(cart.mesh);
}

export function toggleGrabCart(): void {
  cartGrabbed = !cartGrabbed;
}

export function loadFishToCart(): number {
  return transferFish(carryFish, cartFish, CART_CAP);
}

export function unloadBoatToCart(): number {
  return transferFish(boatFish, cartFish, CART_CAP);
}

export function updateCart(dt: number): void {
  if (!cart) return;
  if (cartGrabbed && !inBoat) {
    const tx = player.x - Math.cos(player.heading) * 0.78;
    const tz = player.z - Math.sin(player.heading) * 0.78;
    if (canStand(tx, tz)) {
      const ox = cart.x, oz = cart.z;
      cart.x += (tx - cart.x) * Math.min(1, dt * 7);
      cart.z += (tz - cart.z) * Math.min(1, dt * 7);
      const moved = Math.hypot(cart.x - ox, cart.z - oz);
      (cart.mesh.userData.wheels as THREE.Mesh[]).forEach((wl) => { wl.rotation.z -= moved / 0.11; });
      cart.heading += angleDiff(cart.heading, player.heading) * Math.min(1, dt * 6);
    }
  }
  cart.mesh.position.set(cart.x, standY(cart.x, cart.z), cart.z);
  cart.mesh.rotation.y = -cart.heading;
}

// ================= Storm damage & repairs =================
export const JETTY_REPAIR_COST = 5, BOAT_REPAIR_COST = 6;
export let boatDamaged = false, jettyDamaged = false;

// The list from damage is folded into the hull's roll by the animate loop
// (see main.ts), rather than written here — the swell rewrites the boat's
// rotation every frame, so anything set once would be overwritten.
export function applyBoatDamageVisual(_damaged: boolean): void {
  /* intentionally empty — see boatDamaged in main.ts's boat update */
}
export function applyJettyDamageVisual(damaged: boolean): void {
  jettyGroup.children.forEach((deck) => {
    // Matched on a tag, not on child index — the deck used to be six planks
    // and nothing else, so `i < 6` found them, but it now starts with the
    // runners and that same test was knocking those askew instead.
    deck.children.forEach((m, i) => {
      if (m.userData.isDeckBoard) {
        m.rotation.z = damaged ? ((i % 2 ? 1 : -1) * 0.12) : 0;
        m.rotation.x = damaged ? ((i % 3 ? 1 : -1) * 0.06) : 0;
      }
    });
  });
}

// Rolls storm damage against the boat/jetty; call once per stormy new-day
// transition. Returns which structures were newly damaged this roll.
export function applyStormDamage(): { boat: boolean; jetty: boolean } {
  const hit = { boat: false, jetty: false };
  if (boat && !boatDamaged && Math.random() < 0.8) {
    boatDamaged = true;
    applyBoatDamageVisual(true);
    hit.boat = true;
  }
  if (jettyDecks.size && !jettyDamaged && Math.random() < 0.8) {
    jettyDamaged = true;
    applyJettyDamageVisual(true);
    hit.jetty = true;
  }
  return hit;
}

export function nearJetty(range: number): boolean {
  for (const dk of jettyDecks.keys()) {
    const parts = dk.split(',');
    const pos = hexToWorld(parseInt(parts[0], 10), parseInt(parts[1], 10));
    if (Math.hypot(pos.x - player.x, pos.z - player.z) < range) return true;
  }
  return false;
}

export function repairJetty(): { success: boolean } {
  if (woodCount() < JETTY_REPAIR_COST) return { success: false };
  spendWood(JETTY_REPAIR_COST);
  jettyDamaged = false;
  applyJettyDamageVisual(false);
  return { success: true };
}
export function repairBoat(): { success: boolean } {
  if (woodCount() < BOAT_REPAIR_COST) return { success: false };
  spendWood(BOAT_REPAIR_COST);
  boatDamaged = false;
  applyBoatDamageVisual(false);
  return { success: true };
}
